import { formatLocale, getConstants, separateLocale } from "../..";

interface Image {
	id: number | string;
	filename: string;
	fullpath: string;
}

export type ThumbnailTypes =
	| "default"
	| "default_1080x1080" // 1:1
	| "default_1500x750" // 2:1
	| "default_620x310" // 2:1
	| "default_620x620" // 1:1
	| "default_800x450" // 16:9
	| "instagram_story"
	| "watermark"
	| "watermark_1080x1080" // 1:1
	| "watermark_1200x600" // 2:1
	| "watermark_1280x720" // 16:9
	| "watermark_1440xauto"
	| "watermark_1500x750" // 2:1
	| "watermark_150x75" // 2:1
	| "watermark_155x155" // 1:1
	| "watermark_310x155" // 2:1
	| "watermark_310x310" // 1:1
	| "watermark_336x600" // 14:25
	| "watermark_350x350" // 1:1
	| "watermark_450x800" // 9:16
	| "watermark_600x336" // 25:14
	| "watermark_620x310" // 2:1
	| "watermark_620x620" // 1:1
	| "watermark_620x620_cover" // 1:1
	| "watermark_720x1280" // 9:16
	| "watermark_800x450" // 16:9
	| "watermark_960x480" // 2:1
	| "watermark_autox1440"
	| "watermark_grid_srcset"
	| "watermark_landscape_srcset"
	| "watermark_pinterest_btn_1_1000x1500"
	| "watermark_pinterest_btn_2_1000x1500"
	| "watermark_pinterest_btn_3_1000x1500"
	| "watermark_portrait_autox400"
	| "watermark_portrait_srcset"
	| "watermark_scale_by_width_1280"
	| "watermark_srcset_rectangle"
	| "watermark_srcset_square";

export function getThumbnail(image: Image, locale: string, thumbnail: ThumbnailTypes = "default") {
	if (!image.fullpath) {
		throw new Error(`'image.fullpath' is undefined`);
	}

	if (!(image.id && image.filename)) {
		return image.fullpath;
	}

	const pieces = image.fullpath.split("/");

	const thumbnailPath = `image-thumb__${image.id}__${formatLocale(locale, "language_country")}_${thumbnail}`;
	const fileName = (image.filename ?? pieces[pieces.length - 1]).replace(
		/(.jpg|.jpeg|.png)/gi,
		".webp",
	);

	const pathParts = pieces.splice(0, pieces.length - 1);
	pathParts.push(thumbnailPath);
	pathParts.push(fileName);

	return pathParts.join("/");
}

type AssetProps = {
	path: string;
	locale?: string;
	preFolder?: "default" | "locale" | "locale_with_lang";
	id?: number;
	thumbnail?: ThumbnailTypes;
};

/**
 * *Give path without host and general/locale folder as string or object with path and needed props*
 * @param path - path without host and general/locale folder
 * @param locale - default locale format
 * @param preFolder - when `locale` and `id` are defined, the pre folder path is `locale_with_lang`.
 * Otherwise, it's `default`.
 * - `default`: `General`
 * - `locale`: `TOSIZE.nl`
 * - `locale_with_lang`: `TOSIZE.nl/nl_NL`
 * @param id - image id from pimcore
 * @param thumbnail - thumbnail type from pimcore
 */
export function getLocaleAsset(props: string | AssetProps) {
	const { host, generalFolder } = getConstants().assets;

	if (typeof props === "string") {
		// biome-ignore lint/style/noParameterAssign: This is a valid use case
		props = { path: props };
	}

	const { path, locale, id, thumbnail = "default" } = props;
	const isThumbnail = id !== undefined && locale !== undefined;
	const preFolder = props.preFolder ?? (locale && !isThumbnail ? "locale_with_lang" : "default");

	const countryFolder =
		locale && preFolder !== "default" ? `TOSIZE.${separateLocale(locale).country}` : generalFolder;

	const langFolder =
		locale && preFolder === "locale_with_lang"
			? `${formatLocale(locale, "language_COUNTRY")}`
			: undefined;

	if (isThumbnail) {
		const pieces = path.split("/");
		const thumbnailPath = `image-thumb__${id}__${formatLocale(locale, "language_country")}_${thumbnail}`;
		const fileName = pieces[pieces.length - 1]?.replace(/(.jpg|.jpeg|.png)/gi, ".webp") ?? "";

		const pathParts = pieces.splice(0, pieces.length - 1);
		pathParts.push(thumbnailPath);
		pathParts.push(fileName);
		const joinedPathParts = pathParts.join("/");

		return [host, countryFolder, langFolder, joinedPathParts].filter(Boolean).join("/");
	}

	return [host, countryFolder, langFolder, path].filter(Boolean).join("/");
}
